import { KMainFunctions } from "../../shared/utility/main-functions";
import { isToolBusy, reloadForKind, toolbarTabOf, IDesignerLiveSdAccess } from "./designer-change-routing";
import { DesignerChangeKind, IDesignerLiveEvent } from "./designer-live-types";

/**
 * Designer-live orchestrator (Faz 5): consumes typed change events from the SSE stream and
 * keeps every open designer on the same data.
 *
 * Hybrid refresh: tool idle → silent provider reload; tool busy (open editor) → the reload
 * is DEFERRED — a toast announces who changed what, the toolbar tab gets a badge, and the
 * pending reload applies as soon as the tool goes idle (editor closed / tab re-entered).
 *
 * Also the stale-write source of truth: every change event records (kind|entityId) →
 * {when, who}; editors call markEntityOpened on open and checkStaleWrite before save.
 */

/** Toolbar badge signal — mirrors the MCP_BACKEND_NOTIFICATION_EVENT CustomEvent pattern. */
export const DESIGNER_LIVE_PENDING_EVENT: string = "kuika-designer-live-pending";

export interface IDesignerLivePendingDetail {
  /** toolbar tab names with deferred reloads (empty = all clear) */
  tabs: string[];
}

/** Ordering uses a monotonic tick, not wall-clock — same-millisecond events stay ordered. */
let orderTick: number = 0;

function nextTick(): number {
  orderTick += 1;
  return orderTick;
}

interface IRemoteChangeStamp {
  at: number;
  byUserName: string;
}

interface IOpenedEntity {
  key: string;
  openedAt: number;
}

type SdInstanceGetter = () => IDesignerLiveSdAccess | undefined;

class DesignerSyncServiceImpl {
  private pendingReloads: Set<DesignerChangeKind> = new Set<DesignerChangeKind>();
  private lastRemoteChange: Map<string, IRemoteChangeStamp> = new Map<string, IRemoteChangeStamp>();
  private openedEntity: IOpenedEntity | null = null;
  private reloadInFlight: Set<DesignerChangeKind> = new Set<DesignerChangeKind>();

  private getSd: SdInstanceGetter = () => {
    const w = window as unknown as { getSdInstance?: () => IDesignerLiveSdAccess };
    return typeof w.getSdInstance === "function" ? w.getSdInstance() : undefined;
  };

  public reset = (): void => {
    this.pendingReloads.clear();
    this.lastRemoteChange.clear();
    this.openedEntity = null;
    this.publishPendingBadges();
  };

  public applyRemote = (event: IDesignerLiveEvent, projectId: string): void => {
    if (event.type !== "change" || !event.kind) return;

    const changeKey: string = staleKey(event.kind, event.entityId);
    this.lastRemoteChange.set(changeKey, { at: nextTick(), byUserName: event.byUserName ?? "Another user" });

    const sd = this.getSd();
    if (!sd) return;

    if (isToolBusy(event.kind, sd.state)) {
      this.pendingReloads.add(event.kind);
      this.publishPendingBadges();
      const what: string = event.entityName ? `"${event.entityName}"` : describeKind(event.kind);
      KMainFunctions.displayWarningNotification(
        `${event.byUserName ?? "Another user"} updated ${what}. It will refresh when you close the editor.`
      );
      return;
    }

    void this.runReload(event.kind, projectId);
  };

  /** Called when a tool's editor closes / the tool is re-entered — applies deferred reloads. */
  public onToolIdle = (projectId: string): void => {
    if (this.pendingReloads.size === 0) return;
    const sd = this.getSd();
    if (!sd) return;
    for (const kind of [...this.pendingReloads]) {
      if (!isToolBusy(kind, sd.state)) {
        this.pendingReloads.delete(kind);
        void this.runReload(kind, projectId);
      }
    }
    this.publishPendingBadges();
  };

  private runReload = async (kind: DesignerChangeKind, projectId: string): Promise<void> => {
    if (this.reloadInFlight.has(kind)) return;
    this.reloadInFlight.add(kind);
    try {
      const sd = this.getSd();
      if (!sd) return;
      await reloadForKind(kind, sd, projectId);
    } catch (error: unknown) {
      console.error("[designer-live] applying a remote change failed:", error);
    } finally {
      this.reloadInFlight.delete(kind);
    }
  };

  private publishPendingBadges = (): void => {
    const tabs: string[] = [...this.pendingReloads].map((kind: DesignerChangeKind) => toolbarTabOf(kind));
    const detail: IDesignerLivePendingDetail = { tabs: [...new Set(tabs)] };
    window.dispatchEvent(new CustomEvent(DESIGNER_LIVE_PENDING_EVENT, { detail }));
  };

  // ---- stale-write guard -----------------------------------------------------------------

  /** Editors call this when the user OPENS an entity; doubles as the presence entity marker. */
  public markEntityOpened = (kind: DesignerChangeKind, entityId: string | undefined): void => {
    if (!entityId) {
      this.openedEntity = null;
      return;
    }
    this.openedEntity = { key: staleKey(kind, entityId), openedAt: nextTick() };
  };

  /**
   * True when the entity being saved changed remotely AFTER the user opened it. The caller
   * shows the confirm dialog; this only answers the staleness question (pure, testable).
   */
  public isStaleWrite = (kind: DesignerChangeKind, entityId: string | undefined): IRemoteChangeStamp | null => {
    if (!entityId || !this.openedEntity) return null;
    const key: string = staleKey(kind, entityId);
    if (this.openedEntity.key !== key) return null;
    const stamp: IRemoteChangeStamp | undefined = this.lastRemoteChange.get(key);
    if (!stamp) return null;
    return stamp.at > this.openedEntity.openedAt ? stamp : null;
  };

  /**
   * Save-path gate: resolves true to proceed with the save, false to abort so the user can
   * refresh first. Shows the confirm dialog only when the write is actually stale.
   */
  public checkStaleWrite = (
    kind: DesignerChangeKind,
    entityId: string | undefined,
    entityName: string
  ): Promise<boolean> => {
    const stamp: IRemoteChangeStamp | null = this.isStaleWrite(kind, entityId);
    if (!stamp) return Promise.resolve(true);

    return new Promise<boolean>((resolve: (proceed: boolean) => void) => {
      const proceed: boolean = window.confirm(
        `${stamp.byUserName} changed "${entityName}" after you opened it.\n\n` +
          `OK = save anyway (overwrites their change)\nCancel = go back and refresh first`
      );
      resolve(proceed);
    });
  };
}

function staleKey(kind: DesignerChangeKind, entityId: string | undefined): string {
  return `${kind}|${entityId ?? ""}`;
}

function describeKind(kind: DesignerChangeKind): string {
  switch (kind) {
    case "datasource.changed":
      return "a datasource";
    case "schema.changed":
      return "the data model";
    case "action.changed":
      return "an action";
    case "api.changed":
      return "an API";
    case "workflow.changed":
    case "workflow.lockChanged":
      return "a workflow";
    case "workflowScreens.changed":
      return "workflow screens";
    case "scheduledJob.changed":
      return "a scheduled job";
    case "notificationTemplate.changed":
      return "a notification template";
    case "config.changed":
      return "a configuration";
    case "screenList.changed":
      return "the screen list";
    case "branch.changed":
      return "branches";
    case "project.changed":
      return "project settings";
    default:
      return "shared data";
  }
}

export const DesignerSyncService: DesignerSyncServiceImpl = new DesignerSyncServiceImpl();
